import React, { createContext, useContext, useEffect, useState } from 'react';
import { applyTheme, getTheme, BrandVariant } from '../themes';

interface BrandVariantContextType {
  variant: BrandVariant | 'base';
  setVariant: (variant: BrandVariant | 'base') => void;
  availableVariants: Array<BrandVariant | 'base'>;
}

const BrandVariantContext = createContext<BrandVariantContextType | undefined>(undefined);

const STORAGE_KEY = 'brand-variant-preference';
const AVAILABLE_VARIANTS: Array<BrandVariant | 'base'> = ['base', 'bold', 'minimal', 'editorial', 'tech'];

interface BrandVariantProviderProps {
  children: React.ReactNode;
  defaultVariant?: BrandVariant | 'base';
}

export function BrandVariantProvider({ 
  children, 
  defaultVariant = 'base' 
}: BrandVariantProviderProps) {
  const [variant, setVariantState] = useState<BrandVariant | 'base'>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY) as BrandVariant | 'base' | null;
      if (stored && AVAILABLE_VARIANTS.includes(stored)) {
        return stored;
      }
    }
    return defaultVariant;
  });

  useEffect(() => {
    const theme = getTheme(variant);
    const mode = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    applyTheme(theme, mode);
  }, [variant]);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const mode = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
          const theme = getTheme(variant);
          applyTheme(theme, mode);
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, [variant]);

  const setVariant = (newVariant: BrandVariant | 'base') => {
    if (AVAILABLE_VARIANTS.includes(newVariant)) {
      setVariantState(newVariant);
      localStorage.setItem(STORAGE_KEY, newVariant);
    }
  };

  return (
    <BrandVariantContext.Provider
      value={{
        variant,
        setVariant,
        availableVariants: AVAILABLE_VARIANTS,
      }}
    >
      {children}
    </BrandVariantContext.Provider>
  );
}

export function useBrandVariant() {
  const context = useContext(BrandVariantContext);
  if (context === undefined) {
    throw new Error('useBrandVariant must be used within a BrandVariantProvider');
  }
  return context;
}
